package org.example.project.enginetabular

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.utils.All
import org.example.project.utils.copyTextToClipboard
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.bottom
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.events.Event
import org.w3c.dom.HTMLTextAreaElement

private fun creatorPointState(x: Double, y: Double): All =
    All(popupInitialXPx = x, popupInitialYPx = y)

@Composable
fun TabularCreator() {
    val scope = rememberCoroutineScope()

    var tables by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedTableId by remember { mutableStateOf("") }
    var currentDocument by remember { mutableStateOf(createEmptyDocument()) }
    var tableName by remember { mutableStateOf(currentDocument.name) }
    var statusText by remember { mutableStateOf("Header oder Zelle anklicken, dann Strg+V zum Einfügen.") }
    var busy by remember { mutableStateOf(false) }
    var showNewDialog by remember { mutableStateOf(false) }
    var showSaveDialog by remember { mutableStateOf(false) }
    var showLoadDialog by remember { mutableStateOf(false) }
    var newWidthDraft by remember { mutableStateOf("10") }
    var newHeightDraft by remember { mutableStateOf("10") }
    var saveNameDraft by remember { mutableStateOf(currentDocument.name) }
    var pendingSaveCommit by remember { mutableStateOf<(() -> Unit)?>(null) }
    var selectedHeaderCol by remember { mutableStateOf<Int?>(0) }
    var selectedCell by remember { mutableStateOf<Pair<String, Int>?>(null) }
    var selectedCellRange by remember { mutableStateOf<TabularCellRange?>(null) }
    var selectedRowIndices by remember { mutableStateOf<Set<Int>>(emptySet()) }
    var lastSelectedRowIndex by remember { mutableStateOf<Int?>(null) }
    var contextMenuState by remember { mutableStateOf<All?>(null) }
    var fillHandleState by remember { mutableStateOf<All?>(null) }
    var isDraggingFillHandle by remember { mutableStateOf(false) }
    var pendingFillBottomRow by remember { mutableStateOf<Int?>(null) }
    val creatorRootAttr = "data-tabular-creator-root"

    fun isEditableTarget(target: HTMLElement?): Boolean {
        if (target == null) return false
        if (target is HTMLInputElement || target is HTMLTextAreaElement || target is HTMLSelectElement) return true
        return target.asDynamic().isContentEditable as? Boolean == true
    }

    fun activeSelectionRange(): TabularCellRange? =
        activeSelectionRange(
            selectedCellRange = selectedCellRange,
            selectedRowIndices = selectedRowIndices,
            lastCol = currentDocument.columnSettings.lastIndex.coerceAtLeast(0)
        )

    fun previewSelectionRange(): TabularCellRange? =
        previewSelectionRange(
            baseRange = activeSelectionRange(),
            pendingFillBottomRow = pendingFillBottomRow
        )

    fun copyCurrentSelection() {
        val normalized = ensureRectangular(currentDocument)
        val range = activeSelectionRange()
        val text = when {
            range != null -> creatorSelectionText(normalized, range)
            selectedHeaderCol != null -> normalized.columnSettings.drop(selectedHeaderCol!!).joinToString("\t") { it.label }
            else -> return
        }
        copyTextToClipboard(text)
        statusText = "Auswahl kopiert."
        contextMenuState = null
    }

    fun currentSelectionAsText(): String? {
        val normalized = ensureRectangular(currentDocument)
        val range = activeSelectionRange()
        return when {
            range != null -> creatorSelectionText(normalized, range)
            selectedHeaderCol != null -> normalized.columnSettings
                .drop(selectedHeaderCol!!)
                .joinToString("\t") { it.label }
                .takeIf { it.isNotBlank() }
            else -> null
        }
    }

    fun clearCurrentSelection() {
        val range = activeSelectionRange() ?: return
        currentDocument = clearCreatorRange(currentDocument, range)
        statusText = "Auswahl gelöscht."
        contextMenuState = null
    }

    fun fillSelectionDown(targetBottomRow: Int? = null) {
        val range = activeSelectionRange() ?: return
        val bottomRow = maxOf(range.bottomRow, targetBottomRow ?: range.bottomRow)
        val targetRange = TabularCellRange(range.topRow, range.leftCol, bottomRow, range.rightCol)
        fillCreatorSelection(currentDocument, range, targetBottomRow)?.let { result ->
            currentDocument = creatorResultDocument(result)
            selectedCellRange = targetRange
            statusText = creatorResultStatus(result)
            return
        }
        statusText = "Oben links ist leer."
    }

    suspend fun reloadTables(selectId: String? = null) {
        val loaded = TabularManager.loadUserTables().sortedBy { it.name.lowercase() }
        tables = loaded
        val targetId = selectId?.takeIf { it.isNotBlank() } ?: selectedTableId
        if (targetId.isNotBlank()) {
            loaded.firstOrNull { it.id == targetId }?.let { doc ->
                selectedTableId = doc.id
                currentDocument = ensureRectangular(doc)
                tableName = doc.name
            }
        }
    }

    suspend fun saveCurrent(message: String = "Tabelle gespeichert.") {
        busy = true
        try {
            val saved = TabularManager.saveUserTable(
                ensureRectangular(currentDocument.copy(name = tableName.ifBlank { currentDocument.name }))
            )
            if (saved != null) {
                currentDocument = ensureRectangular(saved)
                tableName = currentDocument.name
                saveNameDraft = currentDocument.name
                selectedTableId = currentDocument.id
                statusText = message
                reloadTables(currentDocument.id)
            } else {
                statusText = "Speichern fehlgeschlagen."
            }
        } finally {
            busy = false
        }
    }

    fun appendRows(count: Int) {
        if (count <= 0) return
        val result = appendCreatorRows(currentDocument, count)
        currentDocument = creatorResultDocument(result)
        statusText = creatorResultStatus(result)
    }

    fun appendColumns(count: Int) {
        if (count <= 0) return
        val result = appendCreatorColumns(currentDocument, count)
        currentDocument = creatorResultDocument(result)
        statusText = creatorResultStatus(result)
    }

    fun removeRows(count: Int) {
        if (count <= 0 || currentDocument.module.isEmpty()) return
        val result = removeCreatorRows(currentDocument, count)
        currentDocument = creatorResultDocument(result)
        statusText = creatorResultStatus(result)
    }

    fun removeColumns(count: Int) {
        if (count <= 0 || currentDocument.columnSettings.isEmpty()) return
        val result = removeCreatorColumns(currentDocument, count)
        currentDocument = creatorResultDocument(result)
        statusText = creatorResultStatus(result)
    }

    fun applyPaste(clipboardText: String) {
        applyCreatorPaste(
            currentDocument = currentDocument,
            selectedHeaderCol = selectedHeaderCol,
            selectedCell = selectedCell,
            selectedCellRange = selectedCellRange,
            text = clipboardText,
            activeSelectionRange = activeSelectionRange()
        )?.let { result ->
            currentDocument = creatorPasteDocument(result)
            statusText = creatorPasteStatus(result)
            if (creatorPasteClearHeaderSelection(result)) selectedHeaderCol = null
            if (creatorPasteClearSelectedCell(result)) selectedCell = null
            if (creatorPasteClearContextMenu(result)) contextMenuState = null
            return
        }
    }

    fun pasteFromClipboard() {
        val clipboard = window.navigator.asDynamic().clipboard ?: return
        clipboard.readText().then({ text ->
            val value = text as? String
            if (!value.isNullOrBlank()) {
                applyPaste(value)
                statusText = "Zwischenablage eingefügt."
            }
        }, {
            statusText = "Einfügen fehlgeschlagen."
            null
        })
        contextMenuState = null
    }

    fun importTableFromText(text: String) {
        importCreatorDocumentFromText(text, currentDocument, tableName)?.let { result ->
            currentDocument = creatorPasteDocument(result)
            statusText = creatorPasteStatus(result)
            return
        }
        statusText = "Importdatei ist leer."
    }

    LaunchedEffect(Unit) {
        reloadTables()
    }

    DisposableEffect(currentDocument, selectedHeaderCol, selectedCell, selectedCellRange, selectedRowIndices) {
        val pasteListener = listener@ { event: Event ->
            val target = event.target as? HTMLElement
            val activeElement = document.activeElement as? HTMLElement
            val insideCreator = (target?.closest("[$creatorRootAttr='true']") != null) ||
                (activeElement?.closest("[$creatorRootAttr='true']") != null)
            if (!insideCreator) return@listener
            if (isEditableTarget(target) || isEditableTarget(activeElement)) return@listener
            val text = event.asDynamic().clipboardData?.getData("text") as? String ?: return@listener
            if (text.isBlank()) return@listener
            event.preventDefault()
            applyPaste(text)
        }
        val copyListener = listener@ { event: Event ->
            val target = event.target as? HTMLElement
            val activeElement = document.activeElement as? HTMLElement
            val insideCreator = (target?.closest("[$creatorRootAttr='true']") != null) ||
                (activeElement?.closest("[$creatorRootAttr='true']") != null)
            if (!insideCreator) return@listener
            if (isEditableTarget(target) || isEditableTarget(activeElement)) return@listener
            val text = currentSelectionAsText() ?: return@listener
            event.preventDefault()
            event.asDynamic().clipboardData?.setData("text/plain", text)
            statusText = "Auswahl kopiert."
        }
        val cutListener = listener@ { event: Event ->
            val target = event.target as? HTMLElement
            val activeElement = document.activeElement as? HTMLElement
            val insideCreator = (target?.closest("[$creatorRootAttr='true']") != null) ||
                (activeElement?.closest("[$creatorRootAttr='true']") != null)
            if (!insideCreator) return@listener
            if (isEditableTarget(target) || isEditableTarget(activeElement)) return@listener
            val text = currentSelectionAsText() ?: return@listener
            event.preventDefault()
            event.asDynamic().clipboardData?.setData("text/plain", text)
            clearCurrentSelection()
            statusText = "Auswahl ausgeschnitten."
        }
        document.addEventListener("paste", pasteListener)
        document.addEventListener("copy", copyListener)
        document.addEventListener("cut", cutListener)
        onDispose {
            document.removeEventListener("paste", pasteListener)
            document.removeEventListener("copy", copyListener)
            document.removeEventListener("cut", cutListener)
        }
    }

    DisposableEffect(selectedCellRange, selectedRowIndices, currentDocument) {
        val root = document.querySelector("[$creatorRootAttr='true']") as? HTMLElement
        val range = activeSelectionRange()
        if (root == null || range == null) {
            fillHandleState = null
            pendingFillBottomRow = null
            onDispose { }
        } else {
            window.setTimeout({
                val rowId = currentDocument.module.getOrNull(range.bottomRow)?.id
                val cell = rowId?.let {
                    root.querySelector("[data-row='$it'][data-col='${range.rightCol}']") as? HTMLElement
                }
                if (cell != null) {
                    val rootRect = root.getBoundingClientRect()
                    val cellRect = cell.getBoundingClientRect()
                    fillHandleState = creatorPointState(
                        x = cellRect.right - rootRect.left - 6.0,
                        y = cellRect.bottom - rootRect.top - 6.0
                    )
                } else {
                    fillHandleState = null
                }
            }, 0)
            onDispose { }
        }
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            contextMenuState = null
        }
        document.addEventListener("click", listener)
        onDispose { document.removeEventListener("click", listener) }
    }

    DisposableEffect(Unit) {
        val moveListener: (Event) -> Unit = listener@{ event ->
            if (!isDraggingFillHandle) return@listener
            val mouseEvent = event.asDynamic()
            val clientY = (mouseEvent.clientY as? Number)?.toDouble() ?: return@listener
            val root = document.querySelector("[$creatorRootAttr='true']") as? HTMLElement ?: return@listener
            val rootRect = root.getBoundingClientRect()
            val relativeY = clientY - rootRect.top
            val rowElements = root.querySelectorAll("[data-row][data-col='0']")
            var targetBottomRow: Int? = null
            for (i in 0 until rowElements.length) {
                val cell = rowElements.item(i) as? HTMLElement ?: continue
                val rect = cell.getBoundingClientRect()
                val cellBottom = rect.bottom - rootRect.top
                if (relativeY >= cellBottom - rect.height / 2.0) {
                    targetBottomRow = i
                }
            }
            if (targetBottomRow != null) {
                pendingFillBottomRow = targetBottomRow
            }
        }
        val upListener: (Event) -> Unit = {
            if (isDraggingFillHandle) {
                fillSelectionDown(pendingFillBottomRow)
            }
            isDraggingFillHandle = false
            pendingFillBottomRow = null
        }
        document.addEventListener("mousemove", moveListener)
        document.addEventListener("mouseup", upListener)
        onDispose {
            document.removeEventListener("mousemove", moveListener)
            document.removeEventListener("mouseup", upListener)
        }
    }

    fun creatorLastCol(): Int = currentDocument.columnSettings.lastIndex.coerceAtLeast(0)

    fun openCreatorContextMenu(clientX: Double, clientY: Double) {
        val root = document.querySelector("[$creatorRootAttr='true']") as? HTMLElement
        val rootRect = root?.getBoundingClientRect()
        contextMenuState = creatorPointState(
            x = clientX - (rootRect?.left ?: 0.0),
            y = clientY - (rootRect?.top ?: 0.0)
        )
    }

    fun selectCreatorHeader(col: Int, status: String) {
        contextMenuState = null
        selectedHeaderCol = col
        selectedCell = null
        selectedCellRange = null
        selectedRowIndices = emptySet()
        lastSelectedRowIndex = null
        statusText = status
    }

    fun selectCreatorRange(range: TabularCellRange, status: String) {
        contextMenuState = null
        selectedCellRange = range
        selectedHeaderCol = null
        selectedRowIndices = emptySet()
        lastSelectedRowIndex = null
        val selectsWholeRow =
            range.leftCol == 0 &&
                range.rightCol >= creatorLastCol()
        val topRowId = currentDocument.module.getOrNull(range.topRow)?.id
        selectedCell = if (!selectsWholeRow && topRowId != null) {
            topRowId to range.leftCol
        } else {
            null
        }
        statusText = status
    }

    fun selectCreatorCell(rowId: String, colIndex: Int, status: String) {
        contextMenuState = null
        selectedCell = rowId to colIndex
        selectedHeaderCol = null
        selectedRowIndices = emptySet()
        lastSelectedRowIndex = null
        selectedCellRange = currentDocument.module.indexOfFirst { it.id == rowId }
            .takeIf { it >= 0 }
            ?.let { rowIndex -> TabularCellRange(rowIndex, colIndex, rowIndex, colIndex) }
        statusText = status
    }

    fun selectCreatorRows(rowIndex: Int, shift: Boolean, ctrl: Boolean) {
        contextMenuState = null
        val nextRows = when {
            shift && lastSelectedRowIndex != null -> {
                val start = minOf(lastSelectedRowIndex!!, rowIndex)
                val end = maxOf(lastSelectedRowIndex!!, rowIndex)
                selectedRowIndices + (start..end).toSet()
            }
            ctrl -> {
                if (rowIndex in selectedRowIndices) selectedRowIndices - rowIndex
                else selectedRowIndices + rowIndex
            }
            else -> setOf(rowIndex)
        }
        selectedRowIndices = nextRows
        lastSelectedRowIndex = rowIndex
        selectedHeaderCol = null
        selectedCell = null
        val sortedRows = nextRows.sorted()
        val contiguous = sortedRows.isNotEmpty() &&
            sortedRows.zipWithNext().all { (a, b) -> b == a + 1 }
        selectedCellRange = if (contiguous && nextRows.isNotEmpty()) {
            val top = nextRows.minOrNull() ?: rowIndex
            val bottom = nextRows.maxOrNull() ?: rowIndex
            TabularCellRange(top, 0, bottom, creatorLastCol())
        } else null
        statusText = "Zeile(n) markiert."
    }

    fun ensureCreatorRowContextSelection(rowIndex: Int) {
        val currentRange = activeSelectionRange()
        val rowAlreadySelected = rowIndex in selectedRowIndices ||
            (currentRange != null && rowIndex in currentRange.topRow..currentRange.bottomRow)
        if (!rowAlreadySelected) {
            selectedRowIndices = setOf(rowIndex)
            selectedCell = null
            selectedHeaderCol = null
            lastSelectedRowIndex = rowIndex
            selectedCellRange = TabularCellRange(rowIndex, 0, rowIndex, creatorLastCol())
        }
    }

    Div({
        attr(creatorRootAttr, "true")
        style {
            width(100.percent)
            height(100.percent)
            boxSizing("border-box")
            overflow("hidden")
            position(org.jetbrains.compose.web.css.Position.Relative)
        }
    }) {
        TabularScreen(
            headers = currentDocument.columnSettings.map { it.key },
            headerDisplayLabels = currentDocument.columnSettings.map { it.label },
            data = TabularManager.toTabularScreenData(currentDocument),
            onCommit = { committed ->
                scope.launch {
                    currentDocument = ensureRectangular(
                        TabularManager.withCommittedRows(
                            document = currentDocument.copy(name = tableName.ifBlank { currentDocument.name }),
                            committedRows = committed
                        )
                    )
                    saveCurrent("Tabelle gespeichert.")
                }
            },
            screenKey = "TabularCreator_${currentDocument.id}",
            title = null,
            editable = true,
            selectionMode = TabularSelectionMode.CELLS,
            createButtonLabel = "Neu",
            saveButtonLabel = "Speichern",
            importButtonLabel = "Excel Import",
            exportButtonLabel = "Excel Export",
            exportFileName = "${tableName.ifBlank { currentDocument.name.ifBlank { "tabelle" } }}.csv",
            csvImportAccept = ".csv,.tsv,.txt",
            toolbarMode = TabularToolbarMode.CREATOR,
            selectedRowIndices = selectedRowIndices,
            selectedCell = selectedCell,
            selectedCellRange = previewSelectionRange(),
            createRow = { MutableList(currentDocument.columnSettings.size) { "" } },
            onCreateAction = {
                newWidthDraft = (currentDocument.columnSettings.size.takeIf { it > 0 } ?: 10).toString()
                newHeightDraft = (currentDocument.module.size.takeIf { it > 0 } ?: 10).toString()
                showNewDialog = true
            },
            onSaveRequest = { commit ->
                pendingSaveCommit = commit
                saveNameDraft = tableName.ifBlank { currentDocument.name }
                showSaveDialog = true
            },
            onCsvImportRequest = { content ->
                importTableFromText(content)
            },
            extraButtons = {
                CreatorToolbarButton(
                    label = "Laden",
                    icon = "images/setup.svg",
                    disabled = busy,
                    onClick = { showLoadDialog = true }
                )
                CreatorToolbarButton(
                    label = "Header",
                    icon = "images/add.svg",
                    onClick = { appendColumns(1) }
                )
                CreatorToolbarButton(
                    label = "Header",
                    icon = "images/remove.svg",
                    onClick = { removeColumns(1) }
                )
                CreatorToolbarButton(
                    label = "Zeile",
                    icon = "images/add.svg",
                    onClick = { appendRows(1) }
                )
                CreatorToolbarButton(
                    label = "Zeile",
                    icon = "images/remove.svg",
                    onClick = { removeRows(1) }
                )
            },
            onHeaderClick = { col ->
                selectCreatorHeader(col, "Header ${col + 1} markiert. Strg+V füllt ab hier zuerst die Header.")
            },
            onHeaderLabelEdit = { col, label ->
                val columns = currentDocument.columnSettings.toMutableList()
                if (col in columns.indices) {
                    columns[col] = columns[col].copy(label = label)
                    currentDocument = currentDocument.copy(columnSettings = columns)
                    selectCreatorHeader(col, "Header ${col + 1} aktualisiert.")
                }
            },
            onRowIndexSelect = { rowIndex, shift, ctrl ->
                selectCreatorRows(rowIndex, shift, ctrl)
            },
            onCellSelectionChange = { range ->
                selectCreatorRange(range, if (
                    range.topRow == range.bottomRow &&
                    range.leftCol == range.rightCol
                ) {
                    "Zelle markiert."
                } else {
                    "Bereich markiert."
                })
            },
            onCellClick = { rowId, colIndex ->
                selectCreatorCell(rowId, colIndex, "Zelle markiert. Strg+V fügt rohe Werte ab dieser Zelle ein.")
            },
            onRowIndexContextMenu = { rowIndex, clientX, clientY ->
                ensureCreatorRowContextSelection(rowIndex)
                openCreatorContextMenu(clientX, clientY)
            },
            onCellContextMenu = { rowId, colIndex, clientX, clientY ->
                val rowIndex = currentDocument.module.indexOfFirst { it.id == rowId }
                if (rowIndex >= 0 && activeSelectionRange()?.contains(rowIndex, colIndex) != true) {
                    selectCreatorCell(rowId, colIndex, "Zelle markiert. Strg+V fügt rohe Werte ab dieser Zelle ein.")
                }
                openCreatorContextMenu(clientX, clientY)
            }
        )

        fillHandleState?.let { pos ->
            Div(attrs = {
                style {
                    position(org.jetbrains.compose.web.css.Position.Absolute)
                    left((pos.popupInitialXPx - 8).px)
                    top((pos.popupInitialYPx - 8).px)
                    width(18.px)
                    height(18.px)
                    padding(0.px)
                    property("z-index", "50")
                    backgroundColor(Color.transparent)
                    property("cursor", if (isDraggingFillHandle) "grabbing" else "grab")
                }
                onMouseDown {
                    it.preventDefault()
                    isDraggingFillHandle = true
                }
            }) {
            }
        }

        contextMenuState?.let { menu ->
            Div({
                style {
                    position(org.jetbrains.compose.web.css.Position.Absolute)
                    left(menu.popupInitialXPx.px)
                    top(menu.popupInitialYPx.px)
                    backgroundColor(Color("#121821"))
                    border(1.px, LineStyle.Solid, Color("#2b3340"))
                    borderRadius(10.px)
                    padding(6.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(4.px)
                    property("z-index", "60")
                }
                onClick { it.stopPropagation() }
            }) {
                CreatorContextMenuItem("Kopieren") { copyCurrentSelection() }
                CreatorContextMenuDivider()
                CreatorContextMenuItem("Löschen") { clearCurrentSelection() }
                CreatorContextMenuDivider()
                CreatorContextMenuItem("Einfügen") { pasteFromClipboard() }
            }
        }

        if (showSaveDialog) {
            CreatorDialog(
                title = "Tabelle speichern",
                onClose = { showSaveDialog = false }
            ) {
                Input(InputType.Text, attrs = {
                    value(saveNameDraft)
                    style {
                        topFieldStyle()
                        width(100.percent)
                    }
                    onInput { saveNameDraft = it.value.orEmpty() }
                })
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        justifyContent(org.jetbrains.compose.web.css.JustifyContent.FlexEnd)
                    }
                }) {
                    LumaticaButton(
                        label = "Abbrechen",
                        onClick = { showSaveDialog = false },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 40,
                        paddingH = 14
                    )
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            tableName = saveNameDraft
                            showSaveDialog = false
                            pendingSaveCommit?.invoke()
                            pendingSaveCommit = null
                        },
                        variant = LumaticaButtonVariant.Action,
                        enabled = !busy && saveNameDraft.isNotBlank(),
                        heightPx = 40,
                        paddingH = 14
                    )
                }
            }
        }

        if (showNewDialog) {
            CreatorDialog(
                title = "Neue Tabelle",
                onClose = { showNewDialog = false }
            ) {
                CreatorDialogRow(label = "Header") {
                    Input(InputType.Number, attrs = {
                        value(newWidthDraft)
                        style {
                            topFieldStyle()
                            width(100.percent)
                        }
                        onInput { newWidthDraft = it.value?.toString().orEmpty() }
                    })
                }
                CreatorDialogRow(label = "Zeilen") {
                    Input(InputType.Number, attrs = {
                        value(newHeightDraft)
                        style {
                            topFieldStyle()
                            width(100.percent)
                        }
                        onInput { newHeightDraft = it.value?.toString().orEmpty() }
                    })
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        justifyContent(org.jetbrains.compose.web.css.JustifyContent.FlexEnd)
                    }
                }) {
                    LumaticaButton(
                        label = "Abbrechen",
                        onClick = { showNewDialog = false },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 40,
                        paddingH = 14
                    )
                    LumaticaButton(
                        label = "Erzeugen",
                        onClick = {
                            val cols = (newWidthDraft.toIntOrNull() ?: 0).coerceAtLeast(1)
                            val rows = (newHeightDraft.toIntOrNull() ?: 0).coerceAtLeast(1)
                            currentDocument = createSizedDocument(
                                name = tableName.ifBlank { "Neue Tabelle" },
                                rows = rows,
                                cols = cols
                            )
                            tableName = currentDocument.name
                            saveNameDraft = currentDocument.name
                            selectedHeaderCol = 0
                            selectedCell = null
                            selectedCellRange = null
                            selectedRowIndices = emptySet()
                            lastSelectedRowIndex = null
                            showNewDialog = false
                            statusText = "Tabelle mit ${cols}x$rows erzeugt."
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 40,
                        paddingH = 14
                    )
                }
            }
        }

        if (showLoadDialog) {
            CreatorDialog(
                title = "Tabelle laden",
                onClose = { showLoadDialog = false }
            ) {
                AppDropdown(
                    options = buildList {
                        add(All(key = "", label = "Tabelle wählen"))
                        addAll(tables.map { table -> All(key = table.id, label = table.name) })
                    },
                    selectedValue = selectedTableId,
                    appearance = AppDropdownAppearance.Field,
                    onChange = { selectedTableId = it },
                    attrs = {
                        style {
                            topFieldStyle()
                            width(100.percent)
                        }
                    }
                )
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        justifyContent(org.jetbrains.compose.web.css.JustifyContent.SpaceBetween)
                    }
                }) {
                    LumaticaButton(
                        label = "Löschen",
                        onClick = {
                            scope.launch {
                                busy = true
                                try {
                                    val ok = TabularManager.deleteUserTable(selectedTableId)
                                    if (ok) {
                                        currentDocument = createEmptyDocument()
                                        tableName = currentDocument.name
                                        saveNameDraft = currentDocument.name
                                        selectedTableId = ""
                                        selectedHeaderCol = 0
                                        selectedCell = null
                                        selectedCellRange = null
                                        selectedRowIndices = emptySet()
                                        lastSelectedRowIndex = null
                                        statusText = "Tabelle gelöscht."
                                        showLoadDialog = false
                                        reloadTables()
                                    } else {
                                        statusText = "Löschen fehlgeschlagen."
                                    }
                                } finally {
                                    busy = false
                                }
                            }
                        },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = !busy && selectedTableId.isNotBlank(),
                        heightPx = 40,
                        paddingH = 14
                    )
                    Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
                        LumaticaButton(
                            label = "Abbrechen",
                            onClick = { showLoadDialog = false },
                            variant = LumaticaButtonVariant.Standard,
                            heightPx = 40,
                            paddingH = 14
                        )
                        LumaticaButton(
                            label = "Laden",
                            onClick = {
                                tables.firstOrNull { it.id == selectedTableId }?.let { doc ->
                                    currentDocument = ensureRectangular(doc)
                                    tableName = doc.name
                                    saveNameDraft = doc.name
                                    selectedCell = null
                                    selectedCellRange = null
                                    selectedHeaderCol = 0
                                    selectedRowIndices = emptySet()
                                    lastSelectedRowIndex = null
                                    statusText = "Tabelle geladen."
                                    pendingSaveCommit = null
                                }
                                showLoadDialog = false
                            },
                            variant = LumaticaButtonVariant.Action,
                            enabled = selectedTableId.isNotBlank(),
                            heightPx = 40,
                            paddingH = 14
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CreatorToolbarButton(
    label: String,
    icon: String,
    disabled: Boolean = false,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
        }
    }) {
        Button(attrs = {
            classes(TableStyles.topBarButton)
            if (disabled) {
                disabled()
                style { property("opacity", "0.4") }
            }
            onMouseEnter { hovered = true }
            onMouseLeave { hovered = false }
            onClick { if (!disabled) onClick() }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Img(src = icon, attrs = {
                classes(TableStyles.topBarIcon)
                style {
                    property(
                        "filter",
                        if (hovered) TabularTheme.ICON_FILTER_ACTIVE else "brightness(1)"
                    )
                }
            })
            Span({
                style {
                    if (hovered) color(TabularTheme.ACCENT)
                    property("white-space", "nowrap")
                }
            }) {
                Text(label)
            }
        }
        Div({ classes(TableStyles.quickDivider) })
    }
}

@Composable
private fun CreatorDialogRow(
    label: String,
    content: @Composable () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Span({
            style {
                width(88.px)
                color(Color("#f5f6f8"))
                fontSize(14.px)
                property("flex-shrink", "0")
            }
        }) { Text(label) }
        Div({ style { flex(1) } }) {
            content()
        }
    }
}

@Composable
private fun CreatorContextMenuItem(
    label: String,
    onClick: () -> Unit
) {
    var hovered by remember { mutableStateOf(false) }
    Div({
        style {
            padding(8.px, 12.px)
            borderRadius(8.px)
            color(if (hovered) TabularTheme.ACCENT else Color("#f5f6f8"))
            backgroundColor(if (hovered) Color("rgba(255,255,255,0.06)") else Color.transparent)
            property("cursor", "pointer")
            property("user-select", "none")
            fontSize(14.px)
        }
        onMouseEnter { hovered = true }
        onMouseLeave { hovered = false }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun CreatorContextMenuDivider() {
    Div({
        style {
            height(1.px)
            backgroundColor(Color("rgba(255,255,255,0.10)"))
            margin(2.px, 0.px)
        }
    })
}

private fun org.jetbrains.compose.web.css.StyleScope.topFieldStyle() {
    height(40.px)
    padding(0.px, 10.px)
    borderRadius(10.px)
    border(1.px, LineStyle.Solid, Color("#2b3340"))
    backgroundColor(Color("#0c1016"))
    color(Color("#f5f6f8"))
    boxSizing("border-box")
}


@Composable
private fun CreatorDialog(
    title: String,
    onClose: () -> Unit,
    content: @Composable () -> Unit
) {
    Div({
        style {
            position(org.jetbrains.compose.web.css.Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.58)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
            property("z-index", "12000")
        }
    }) {
        Div({
            style {
                width(460.px)
                padding(18.px)
                borderRadius(14.px)
                backgroundColor(Color("#121821"))
                border(1.px, LineStyle.Solid, Color("#2b3340"))
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
                boxSizing("border-box")
            }
        }) {
            H2({ style { margin(0.px); fontSize(22.px); color(Color("#f5f6f8")) } }) { Text(title) }
            content()
        }
    }
}
